import type { FC, ReactNode, MouseEvent } from 'react';
import { useEffect, useRef } from 'react';
import { Icon } from '@iconify/react';
import { useNavigate } from 'react-router-dom';
import toastValidator from '@components/interface/ToastValidator';
import { useDesktop } from '@utils/hooks/useDesktop';

type ModalComponentProps = {
  title?: string;
  subtitle?: string;
  description?: string;
  action?: ReactNode;
  confirmExit?: boolean;
  children: ReactNode;
  onClose?: () => void;
  disableManualClose?: boolean;
};

const ModalComponent: FC<ModalComponentProps> = ({
  title,
  subtitle,
  description,
  action,
  confirmExit,
  children,
  onClose,
  disableManualClose = false,
}) => {
  const navigate = useNavigate();
  const modalRef = useRef<HTMLDivElement>(null);
  const bgRef = useRef<HTMLDivElement>(null);
  const isDesktop = useDesktop();

  const requestClose = (e: MouseEvent<HTMLElement>, force?: boolean) => {
    if (force || (bgRef && bgRef.current && e.target === bgRef.current)) {
      const close = () => {
        if (bgRef && bgRef.current) {
          bgRef.current.classList.add('opacity-0');
        }
        if (modalRef && modalRef.current) {
          modalRef.current.classList.add('translate-y-[50%]');
          modalRef.current.addEventListener(
            'transitionend',
            () => {
              if (onClose) {
                onClose();
              } else {
                navigate('..');
              }
            },
            { once: true },
          );
        }
      };

      if (confirmExit) {
        toastValidator({ message: 'Los cambios se perderán\n¿Desea salir?', onValidate: close });
      } else close();
    }
  };

  useEffect(() => {
    requestAnimationFrame(() => {
      if (modalRef && modalRef.current) {
        modalRef.current.classList.remove('translate-y-[50%]');
      }
      if (bgRef && bgRef.current) {
        bgRef.current.classList.remove('opacity-0');
      }
    });
  }, []);

  return (
    <div
      ref={bgRef}
      className="fixed w-full h-full top-0 left-0 text-color-primary bg-color-primary_dark/[0.7] z-[1000] flex justify-center items-center opacity-0 transition-opacity duration-300"
      onClick={disableManualClose ? () => null : requestClose}
    >
      <div
        ref={modalRef}
        className="relative rounded-md bg-color-white max-h-[95%] max-w-[93%] lg:max-h-[95%] lg:max-w-[90%] xl:max-w-[70%] translate-y-[50%] transition-transform ease-out duration-300"
      >
        {disableManualClose ? null : (
          <button
            onClick={(e) => requestClose(e, true)}
            className="absolute top-0 right-0 bg-color-secondary rounded-full w-9 h-9 -mr-3 -mt-3"
          >
            <Icon className="text-4xl font-bold text-color-white" icon="eva:close-fill" />
          </button>
        )}
        <div className="my-5">
          {(title || subtitle || description || action) && (
            <>
              {isDesktop ? (
                <div className="flex justify-between mx-5">
                  <div className="mr-2">
                    {title && (
                      <div className="flex">
                        <h4 className="text-xl font-bold text-color-primary inline-block">{title}</h4>
                      </div>
                    )}
                    {(subtitle || description) && (
                      <div className="mb-2.5 text-sm">
                        {subtitle && <p className="text-color-secondary font-bold text-base">{subtitle}</p>}
                        {description && <p>{description}</p>}
                      </div>
                    )}
                  </div>
                  {action}
                </div>
              ) : (
                <div className="flex justify-between mx-5">
                  <div className="mr-2 w-full">
                    <div className="flex justify-between">
                      {title && <h4 className="w-full text-xl font-bold text-color-primary inline-block">{title}</h4>}
                      {action}
                    </div>
                    {(subtitle || description) && (
                      <div className="mb-2.5 text-sm">
                        {subtitle && <p className="text-color-secondary font-bold text-base">{subtitle}</p>}
                        {description && <p>{description}</p>}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
          <div className="flex flex-col mx-5 mb-2.5 overflow-hidden max-h-[calc(100vh_-_10rem)]">{children}</div>
        </div>
      </div>
    </div>
  );
};

export default ModalComponent;
